import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import ReviewsStateStatisticsFragment
	from '@app/site/secured/review-manager/fragment/property-overview/component/ReviewsStateStatisticsFragment';
import Utilities from '@util/Utilities';
import ReviewsPropertyInsightsCategoryPerformance
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsightsCategoryPerformance';
import ReviewsPropertyInsightsKeyObservations
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsightsKeyObservations';
import ReviewsPropertyInsightsCompetitiveAnalysis
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsightsCompetitiveAnalysis';
import ReviewsCompetitorInsightsKeyObservations
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsCompetitorInsightsKeyObservations';

const ReviewsPropertyInsightsWeeklyDigest = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: weeklyDigest, isSuccess, isLoading, isError } = useQuery({
		queryKey: ReviewsQueryKeys.weeklyDigest(companyHotel.id),
		queryFn: () => ReviewService.findReviewsWeeklyDigest(companyHotel.id),
	});

	if (isLoading)
		return <TablePlaceholder />;

	if (isError)
		return <Message state="error">The weekly digest could not be loaded</Message>;

	if (isSuccess) {
		const current = weeklyDigest?.current;
		const previous = weeklyDigest?.previous;
		const currentMainHotelState = current?.findMainHotelState();
		if (!!weeklyDigest && !!current && !!currentMainHotelState) {
			const currentPeriod = current.from != null && current.to != null ? `${Utilities.formatDateString(current.from, 'MMM DD')} - ${Utilities.formatDateString(current.to, 'MMM DD')}` : '';
			const previousPeriod = previous?.from != null && previous.to != null ? `${Utilities.formatDateString(previous.from, 'MMM DD')} - ${Utilities.formatDateString(previous.to, 'MMM DD')}` : '';
			const previousMainHotelState = previous?.findMainHotelState();

			return <>
				<ReviewsStateStatisticsFragment
					statistics={currentMainHotelState.statistics}
					rank={current.rank}
					previousStatistics={previousMainHotelState?.statistics}
					variant="insightsDigest"
				/>
				<div className="flex items-center mb-4">
					<div className="relative flex shrink-0 justify-start">
						<span className="bg-gray-100 text-sm font-normal text-gray-500 pr-3">
							{currentPeriod}
							{previousPeriod !== '' && (
								<span
									className="ml-2">vs. {previousPeriod}</span>
							)}
						</span>
					</div>
					<div className="w-full border-t border-gray-300"></div>
				</div>
				<div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
					<ReviewsPropertyInsightsCategoryPerformance digest={weeklyDigest} />
					<ReviewsPropertyInsightsKeyObservations digest={weeklyDigest} />
					<ReviewsPropertyInsightsCompetitiveAnalysis digest={weeklyDigest} />
					<ReviewsCompetitorInsightsKeyObservations digest={weeklyDigest} />
				</div>
			</>;
		} else {
			return <Message state="error">The weekly digest could not be found</Message>;
		}
	}

	return <div>Weekly Digest</div>;
});

export default ReviewsPropertyInsightsWeeklyDigest;
